// Pure, dependency-free math helpers for the browser math calculators
// (probability, confidence interval, average/stats, and fractions).
// Everything here is deterministic and runs locally in the browser.

// ---------------------------------------------------------------------------
// Shared number helpers
// ---------------------------------------------------------------------------

/** Format a number without trailing noise, capped at `digits` decimals. */
export function formatNumber(value: number, digits = 6): string {
  if (!Number.isFinite(value)) return '0';
  const rounded = Number(value.toFixed(digits));
  return rounded.toLocaleString('en-US', { maximumFractionDigits: digits });
}

/** Parse a single numeric field. Returns null when it is blank or invalid. */
export function parseFiniteNumber(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

// ---------------------------------------------------------------------------
// Probability
// ---------------------------------------------------------------------------

/**
 * Parse a probability entered as a decimal (0.25) or a percentage ("25%").
 * Returns null when blank/invalid and clamps into the [0, 1] range otherwise.
 */
export function parseProbability(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const isPercent = trimmed.endsWith('%');
  const numeric = Number(trimmed.replace('%', '').trim());
  if (!Number.isFinite(numeric)) return null;
  const value = isPercent ? numeric / 100 : numeric;
  if (value < 0 || value > 1) return null;
  return value;
}

export type ProbabilityResult = {
  label: string;
  value: string;
  note?: string;
};

/** Single-event probability from favorable / total counts. */
export function singleEventProbability(
  favorable: number | null,
  total: number | null,
): ProbabilityResult[] | { error: string } {
  if (favorable === null || total === null) return [];
  if (total <= 0) return { error: 'Total outcomes must be greater than zero.' };
  if (favorable < 0) return { error: 'Favorable outcomes cannot be negative.' };
  if (favorable > total) return { error: 'Favorable outcomes cannot exceed total outcomes.' };
  const p = favorable / total;
  const notP = 1 - p;
  const oddsAgainst = p === 0 ? '—' : `${formatNumber(notP / p, 3)} : 1`;
  return [
    {
      label: 'P(event)',
      value: formatNumber(p, 6),
      note: `${favorable} favorable / ${total} total`,
    },
    { label: 'As a percentage', value: `${formatNumber(p * 100, 4)}%` },
    { label: 'P(not event)', value: formatNumber(notP, 6), note: 'Complement: 1 − P' },
    { label: 'Odds against', value: oddsAgainst },
  ];
}

/**
 * Combined probabilities for two events. When P(A and B) is supplied it is
 * used directly; otherwise the events are treated as independent.
 */
export function combinedProbabilities(
  pA: number | null,
  pB: number | null,
  pAndGiven: number | null,
): ProbabilityResult[] {
  if (pA === null || pB === null) return [];
  const independent = pAndGiven === null;
  const pAnd = independent ? pA * pB : (pAndGiven as number);
  const pOr = pA + pB - pAnd;
  const results: ProbabilityResult[] = [
    {
      label: 'P(A and B)',
      value: formatNumber(pAnd, 6),
      note: independent ? 'Independent: P(A) × P(B)' : 'Using the value you entered',
    },
    {
      label: 'P(A or B)',
      value: formatNumber(Math.min(Math.max(pOr, 0), 1), 6),
      note: 'P(A) + P(B) − P(A and B)',
    },
    { label: 'P(not A)', value: formatNumber(1 - pA, 6), note: 'Complement of A' },
    { label: 'P(not B)', value: formatNumber(1 - pB, 6), note: 'Complement of B' },
  ];
  if (pB > 0) {
    results.push({
      label: 'P(A given B)',
      value: formatNumber(pAnd / pB, 6),
      note: 'P(A and B) ÷ P(B)',
    });
  }
  if (pA > 0) {
    results.push({
      label: 'P(B given A)',
      value: formatNumber(pAnd / pA, 6),
      note: 'P(A and B) ÷ P(A)',
    });
  }
  return results;
}

// ---------------------------------------------------------------------------
// Confidence interval (z-approximation)
// ---------------------------------------------------------------------------

/** Inverse standard-normal CDF (Peter Acklam's rational approximation). */
export function inverseNormalCdf(p: number): number {
  if (p <= 0) return Number.NEGATIVE_INFINITY;
  if (p >= 1) return Number.POSITIVE_INFINITY;
  const a = [
    -3.969683028665376e1, 2.209460984245205e2, -2.759285104469687e2, 1.38357751867269e2,
    -3.066479806614716e1, 2.506628277459239,
  ];
  const b = [
    -5.447609879822406e1, 1.615858368580409e2, -1.556989798598866e2, 6.680131188771972e1,
    -1.328068155288572e1,
  ];
  const c = [
    -7.784894002430293e-3, -3.223964580411365e-1, -2.400758277161838, -2.549732539343734,
    4.374664141464968, 2.938163982698783,
  ];
  const d = [7.784695709041462e-3, 3.224671290700398e-1, 2.445134137142996, 3.754408661907416];
  const plow = 0.02425;
  const phigh = 1 - plow;
  if (p < plow) {
    const q = Math.sqrt(-2 * Math.log(p));
    return (
      (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
      ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1)
    );
  }
  if (p <= phigh) {
    const q = p - 0.5;
    const r = q * q;
    return (
      ((((((a[0] * r + a[1]) * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) * q) /
      (((((b[0] * r + b[1]) * r + b[2]) * r + b[3]) * r + b[4]) * r + 1)
    );
  }
  const q = Math.sqrt(-2 * Math.log(1 - p));
  return -(
    (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
    ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1)
  );
}

/** Two-sided z critical value for a confidence level given as a percent. */
export function zForConfidence(confidencePercent: number): number {
  const c = confidencePercent / 100;
  const alpha = 1 - c;
  return inverseNormalCdf(1 - alpha / 2);
}

export type ConfidenceIntervalResult = {
  z: number;
  standardError: number;
  marginOfError: number;
  lower: number;
  upper: number;
};

export function confidenceInterval(
  mean: number | null,
  stdDev: number | null,
  sampleSize: number | null,
  confidencePercent: number,
): ConfidenceIntervalResult | { error: string } | null {
  if (mean === null || stdDev === null || sampleSize === null) return null;
  if (sampleSize < 1) return { error: 'Sample size must be at least 1.' };
  if (!Number.isInteger(sampleSize)) return { error: 'Sample size must be a whole number.' };
  if (stdDev < 0) return { error: 'Standard deviation cannot be negative.' };
  if (confidencePercent <= 0 || confidencePercent >= 100) {
    return { error: 'Confidence level must be between 0 and 100.' };
  }
  const z = zForConfidence(confidencePercent);
  const standardError = stdDev / Math.sqrt(sampleSize);
  const marginOfError = z * standardError;
  return {
    z,
    standardError,
    marginOfError,
    lower: mean - marginOfError,
    upper: mean + marginOfError,
  };
}

// ---------------------------------------------------------------------------
// Average / descriptive statistics
// ---------------------------------------------------------------------------

export type NumberListParse = {
  numbers: number[];
  ignored: string[];
};

/** Split a free-text list on commas, whitespace and newlines. */
export function parseNumberList(raw: string): NumberListParse {
  const tokens = raw.split(/[\s,;]+/).filter((token) => token.length > 0);
  const numbers: number[] = [];
  const ignored: string[] = [];
  for (const token of tokens) {
    const parsed = Number(token);
    if (Number.isFinite(parsed)) {
      numbers.push(parsed);
    } else {
      ignored.push(token);
    }
  }
  return { numbers, ignored };
}

export type StatsResult = {
  count: number;
  sum: number;
  mean: number;
  median: number;
  mode: string;
  range: number;
  min: number;
  max: number;
  populationStdDev: number;
  sampleStdDev: number;
};

export function computeStats(values: number[]): StatsResult | null {
  const n = values.length;
  if (n === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const sum = values.reduce((acc, v) => acc + v, 0);
  const mean = sum / n;
  const median = n % 2 === 1 ? sorted[(n - 1) / 2] : (sorted[n / 2 - 1] + sorted[n / 2]) / 2;

  // Mode: values sharing the highest frequency (>1). None when all unique.
  const counts = new Map<number, number>();
  for (const v of values) counts.set(v, (counts.get(v) ?? 0) + 1);
  let maxFreq = 0;
  for (const freq of counts.values()) if (freq > maxFreq) maxFreq = freq;
  let mode = 'No mode (all unique)';
  if (maxFreq > 1) {
    const modes = [...counts.entries()]
      .filter(([, freq]) => freq === maxFreq)
      .map(([value]) => value)
      .sort((a, b) => a - b);
    mode = modes.map((v) => formatNumber(v, 6)).join(', ');
  }

  const min = sorted[0];
  const max = sorted[n - 1];
  const variancePop = values.reduce((acc, v) => acc + (v - mean) ** 2, 0) / n;
  const populationStdDev = Math.sqrt(variancePop);
  const sampleStdDev = n > 1 ? Math.sqrt((variancePop * n) / (n - 1)) : 0;

  return {
    count: n,
    sum,
    mean,
    median,
    mode,
    range: max - min,
    min,
    max,
    populationStdDev,
    sampleStdDev,
  };
}

// ---------------------------------------------------------------------------
// Fractions
// ---------------------------------------------------------------------------

export function gcd(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) {
    [x, y] = [y, x % y];
  }
  return x || 1;
}

export type Fraction = { numerator: number; denominator: number };

/** Combine an optional whole part with a numerator/denominator into one fraction. */
export function toImproper(whole: number, numerator: number, denominator: number): Fraction {
  // The whole part carries the sign; with no whole part the numerator's sign
  // is used so that entries like -1/2 stay negative.
  const sign = whole < 0 || (whole === 0 && numerator < 0) ? -1 : 1;
  const combined = Math.abs(whole) * Math.abs(denominator) + Math.abs(numerator);
  return { numerator: sign * combined, denominator: Math.abs(denominator) };
}

export function reduceFraction(fraction: Fraction): Fraction {
  let { numerator, denominator } = fraction;
  if (denominator < 0) {
    numerator = -numerator;
    denominator = -denominator;
  }
  const divisor = gcd(numerator, denominator);
  return { numerator: numerator / divisor, denominator: denominator / divisor };
}

export type FractionOp = 'add' | 'subtract' | 'multiply' | 'divide';

export type FractionResult = {
  raw: Fraction;
  reduced: Fraction;
  decimal: number;
  mixed: string;
  steps: string[];
};

const OP_SYMBOL: Record<FractionOp, string> = {
  add: '+',
  subtract: '−',
  multiply: '×',
  divide: '÷',
};

function fractionText(f: Fraction): string {
  return `${f.numerator}/${f.denominator}`;
}

function mixedText(f: Fraction): string {
  const { numerator, denominator } = f;
  if (denominator === 0) return '—';
  if (numerator % denominator === 0) return `${numerator / denominator}`;
  const sign = numerator < 0 ? '-' : '';
  const absNum = Math.abs(numerator);
  const whole = Math.floor(absNum / denominator);
  const rem = absNum % denominator;
  if (whole === 0) return `${sign}${rem}/${denominator}`;
  return `${sign}${whole} ${rem}/${denominator}`;
}

export function computeFraction(
  a: Fraction,
  b: Fraction,
  op: FractionOp,
): FractionResult | { error: string } {
  if (a.denominator === 0 || b.denominator === 0) {
    return { error: 'Denominators cannot be zero.' };
  }
  if (op === 'divide' && b.numerator === 0) {
    return { error: 'Cannot divide by a fraction equal to zero.' };
  }

  const steps: string[] = [];
  steps.push(`Fraction A = ${fractionText(a)},  Fraction B = ${fractionText(b)}`);

  let raw: Fraction;
  switch (op) {
    case 'add':
    case 'subtract': {
      const commonDen = a.denominator * b.denominator;
      const aNum = a.numerator * b.denominator;
      const bNum = b.numerator * a.denominator;
      const num = op === 'add' ? aNum + bNum : aNum - bNum;
      steps.push(`Common denominator: ${a.denominator} × ${b.denominator} = ${commonDen}`);
      steps.push(
        `Rewrite: ${aNum}/${commonDen} ${OP_SYMBOL[op]} ${bNum}/${commonDen} = ${num}/${commonDen}`,
      );
      raw = { numerator: num, denominator: commonDen };
      break;
    }
    case 'multiply': {
      raw = { numerator: a.numerator * b.numerator, denominator: a.denominator * b.denominator };
      steps.push(
        `Multiply across: (${a.numerator} × ${b.numerator}) / (${a.denominator} × ${b.denominator}) = ${fractionText(raw)}`,
      );
      break;
    }
    case 'divide': {
      raw = { numerator: a.numerator * b.denominator, denominator: a.denominator * b.numerator };
      steps.push(
        `Divide = multiply by the reciprocal: ${fractionText(a)} × ${b.denominator}/${b.numerator} = ${fractionText(raw)}`,
      );
      break;
    }
  }

  const reduced = reduceFraction(raw);
  const divisor = gcd(raw.numerator, raw.denominator);
  if (divisor > 1) {
    steps.push(`Simplify by dividing top and bottom by ${divisor}: ${fractionText(reduced)}`);
  } else {
    steps.push('Already in lowest terms.');
  }

  const decimal = reduced.denominator === 0 ? 0 : reduced.numerator / reduced.denominator;
  return {
    raw,
    reduced,
    decimal,
    mixed: mixedText(reduced),
    steps,
  };
}
